import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBriefcase,
  FaCheckCircle,
  FaEnvelope,
  FaIdBadge,
  FaKey,
  FaRedo,
  FaSave,
  FaSearch,
  FaShieldAlt,
  FaTimes,
  FaUser,
  FaUserTie
} from 'react-icons/fa';
import Sidebar from '../../components/Sidebar/sidebar';
import Header from '../../components/Header';
import api from '../../utils/api';
import "./Register.css";
import { API_BASE_URL } from '../../utils/apiBase';

const titleCase = (value = '') =>
  value
    .replace(/([A-Z])/g, ' $1')
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const Register = () => {
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('user');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [generatedEmpId, setGeneratedEmpId] = useState('');
  const [loading, setLoading] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [roleId, setRoleId] = useState('');
  const [reportingManagerId, setReportingManagerId] = useState('');
  const [managerSearch, setManagerSearch] = useState('');
  const [managerRoleFilter, setManagerRoleFilter] = useState('all');
  const [managerPickerOpen, setManagerPickerOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    let active = true;

    const loadEmployees = async () => {
      try {
        const res = await api.get(`${API_BASE_URL}/api/auth/employee-options`);
        if (active) {
          setEmployeeOptions(res.data || []);
        }
      } catch (err) {
        if (active) {
          setError('Failed to load reporting managers.');
        }
      }
    };

    loadEmployees();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadRoles = async () => {
      try {
        const res = await api.get(`${API_BASE_URL}/api/roles/options`);
        if (active) {
          setRoleOptions(res.data || []);
        }
      } catch (err) {
        if (active) {
          setError('Failed to load roles.');
        }
      }
    };

    loadRoles();

    return () => {
      active = false;
    };
  }, []);

  const selectedManager = employeeOptions.find((employee) => employee._id === reportingManagerId);

  const managerRoleOptions = useMemo(
    () => [
      ...new Set(
        employeeOptions.map((employee) => employee.roleName || titleCase(employee.role || 'User'))
      )
    ].sort((a, b) => a.localeCompare(b)),
    [employeeOptions]
  );

  const filteredManagers = useMemo(() => {
    const search = managerSearch.trim().toLowerCase();

    return employeeOptions.filter((employee) => {
      const displayRole = employee.roleName || titleCase(employee.role || 'User');
      const searchText = [
        employee.name,
        employee.username,
        employee.employeeId,
        displayRole,
        employee.role
      ]
        .join(' ')
        .toLowerCase();
      const matchesSearch = !search || searchText.includes(search);
      const matchesRole = managerRoleFilter === 'all' || displayRole === managerRoleFilter;
      return matchesSearch && matchesRole;
    });
  }, [employeeOptions, managerRoleFilter, managerSearch]);

  const resetForm = () => {
    setName('');
    setUsername('');
    setEmail('');
    setPassword('');
    setRole('user');
    setRoleId('');
    setReportingManagerId('');
    setManagerSearch('');
    setManagerRoleFilter('all');
    setManagerPickerOpen(false);
    setFieldErrors({});
    setError('');
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!name.trim()) nextErrors.name = 'Name is required.';
    if (!username.trim()) nextErrors.username = 'Username is required.';
    if (!email.trim()) {
      nextErrors.email = 'Email is required.';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      nextErrors.email = 'Enter a valid email address.';
    }
    if (!password) {
      nextErrors.password = 'Password is required.';
    } else if (password.length < 6) {
      nextErrors.password = 'Use at least 6 characters.';
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const res = await api.post(
        `${API_BASE_URL}/api/auth/register`,
        { name, username, email, password, role, roleId: roleId || null, reportingManagerId }
      );

      setGeneratedEmpId(res.data.employeeId);
      setShowPopup(true);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.error || "Error registering user");
    } finally {
      setLoading(false);
    }
  };

  const selectManager = (employee) => {
    setReportingManagerId(employee._id);
    setManagerPickerOpen(false);
  };

  return (
    <div className="register-container">
      <div className="sidebar">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>
      <div className={`register-content ${collapsed ? 'collapsed' : ''}`}>
        <div className="sticky-header">
          <Header />
        </div>
        <div className="register-scroll-area">
          <section className="register-shell">
            <div className="register-heading">
              <div>
                <span><FaUserTie /> Employee Administration</span>
                <h2>Create User</h2>
                <p>Register employees, assign access levels, connect roles, and set reporting lines.</p>
              </div>
              <div className="register-heading-card">
                <strong>{roleOptions.length}</strong>
                <span>active roles</span>
              </div>
            </div>

            {error && <p className="register-alert">{error}</p>}

            <form className="register-form" onSubmit={handleRegister} autoComplete="off">
              <div className="register-form-grid">
                <section className="register-card">
                  <div className="register-section-title">
                    <FaUser />
                    <div>
                      <h3>Personal Details</h3>
                      <p>Core employee identity information.</p>
                    </div>
                  </div>

                  <div className="register-field-grid">
                    <div className="register-form-group">
                      <label>Name <span className="required-mark">*</span></label>
                      <div className={`register-input-shell ${fieldErrors.name ? 'invalid' : ''}`}>
                        <FaUser />
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="John David"
                        />
                      </div>
                      {fieldErrors.name && <small>{fieldErrors.name}</small>}
                    </div>

                    <div className="register-form-group">
                      <label>Email <span className="required-mark">*</span></label>
                      <div className={`register-input-shell ${fieldErrors.email ? 'invalid' : ''}`}>
                        <FaEnvelope />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="john@company.com"
                        />
                      </div>
                      {fieldErrors.email && <small>{fieldErrors.email}</small>}
                    </div>
                  </div>
                </section>

                <section className="register-card">
                  <div className="register-section-title">
                    <FaIdBadge />
                    <div>
                      <h3>Employee Details</h3>
                      <p>Credentials and reporting structure.</p>
                    </div>
                  </div>

                  <div className="register-field-grid">
                    <div className="register-form-group">
                      <label>Username <span className="required-mark">*</span></label>
                      <div className={`register-input-shell ${fieldErrors.username ? 'invalid' : ''}`}>
                        <FaIdBadge />
                        <input
                          type="text"
                          name="newEmployeeUsername"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          placeholder="john.david"
                          autoComplete="off"
                          data-lpignore="true"
                          data-1p-ignore="true"
                        />
                      </div>
                      {fieldErrors.username && <small>{fieldErrors.username}</small>}
                    </div>

                    <div className="register-form-group">
                      <label>Password <span className="required-mark">*</span></label>
                      <div className={`register-input-shell ${fieldErrors.password ? 'invalid' : ''}`}>
                        <FaKey />
                        <input
                          type="password"
                          name="newEmployeePassword"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Minimum 6 characters"
                          autoComplete="new-password"
                          data-lpignore="true"
                          data-1p-ignore="true"
                        />
                      </div>
                      {fieldErrors.password && <small>{fieldErrors.password}</small>}
                    </div>
                  </div>

                  <div className="register-form-group full">
                    <label>Reporting Manager</label>
                    <div className="manager-picker">
                      <button
                        type="button"
                        className={`manager-picker-trigger ${managerPickerOpen ? 'open' : ''}`}
                        onClick={() => setManagerPickerOpen((current) => !current)}
                      >
                        <FaUserTie />
                        <span>
                          {selectedManager
                            ? `${selectedManager.name} - ${selectedManager.employeeId || 'No ID'} - ${selectedManager.roleName || titleCase(selectedManager.role || 'User')}`
                            : 'Select Reporting Manager'}
                        </span>
                      </button>

                      {managerPickerOpen && (
                        <div className="manager-dropdown">
                          <div className="manager-filter-row">
                            <label className="manager-search">
                              <FaSearch />
                              <input
                                value={managerSearch}
                                onChange={(e) => setManagerSearch(e.target.value)}
                                placeholder="Search name or employee ID"
                              />
                            </label>
                            <select
                              value={managerRoleFilter}
                              onChange={(e) => setManagerRoleFilter(e.target.value)}
                            >
                              <option value="all">All Roles</option>
                              {managerRoleOptions.map((managerRole) => (
                                <option key={managerRole} value={managerRole}>
                                  {managerRole}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="manager-list">
                            <button
                              type="button"
                              className={`manager-row ${!reportingManagerId ? 'selected' : ''}`}
                              onClick={() => {
                                setReportingManagerId('');
                                setManagerPickerOpen(false);
                              }}
                            >
                              <span>No Reporting Manager</span>
                              <span>-</span>
                              <span>Optional</span>
                            </button>
                            {filteredManagers.map((employee) => {
                              const displayRole = employee.roleName || titleCase(employee.role || 'User');
                              return (
                                <button
                                  type="button"
                                  key={employee._id}
                                  className={`manager-row ${reportingManagerId === employee._id ? 'selected' : ''}`}
                                  onClick={() => selectManager(employee)}
                                >
                                  <span>{employee.name}</span>
                                  <span>{employee.employeeId || '-'}</span>
                                  <span>{displayRole}</span>
                                </button>
                              );
                            })}
                            {filteredManagers.length === 0 && (
                              <div className="manager-empty">No employees found.</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                <section className="register-card">
                  <div className="register-section-title">
                    <FaShieldAlt />
                    <div>
                      <h3>Access & Role Details</h3>
                      <p>Access level controls hierarchy. Role controls privileges.</p>
                    </div>
                  </div>

                  <div className="register-field-grid">
                    <div className="register-form-group">
                      <label>Access Level <span className="required-mark">*</span></label>
                      <div className="register-input-shell">
                        <FaShieldAlt />
                        <select value={role} onChange={(e) => setRole(e.target.value)}>
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>

                    <div className="register-form-group">
                      <label>Role</label>
                      <div className="register-input-shell">
                        <FaBriefcase />
                        <select value={roleId} onChange={(e) => setRoleId(e.target.value)}>
                          <option value="">Select Role</option>
                          {roleOptions.map((roleOption) => (
                            <option key={roleOption._id} value={roleOption._id}>
                              {roleOption.roleName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </section>
              </div>

              <div className="register-actions">
                <button type="button" className="ghost" onClick={() => navigate('/dashboard')}>
                  <FaTimes /> Cancel
                </button>
                <button type="button" onClick={resetForm}>
                  <FaRedo /> Reset
                </button>
                <button className="register-button" type="submit" disabled={loading}>
                  {loading ? <span className="register-spinner" /> : <FaSave />}
                  {loading ? 'Registering...' : 'Register'}
                </button>
              </div>
            </form>
          </section>
        </div>

        {showPopup && (
          <div className="register-modal-overlay">
            <div className="register-modal">
              <FaCheckCircle className="success-icon" />
              <h3>User Registered Successfully</h3>
              <p><strong>Employee ID:</strong> {generatedEmpId}</p>

              <button
                onClick={() => {
                  setShowPopup(false);
                  resetForm();
                  navigate('/register');
                }}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;
